import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../components/auth/AuthProvider';

export type AuthView = 'sign_in' | 'sign_up' | 'forgotten_password' | 'update_password';

export type MessageType = 'success' | 'warn';
export type Message = {
  text: string;
  type: MessageType;
} | null;

export function useAuthForm() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const resetForm = useCallback(() => {
    setEmail('');
    setPassword('');
    setLoading(false);
  }, []);

  const handleSignIn = useCallback(async () => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      // Check if the error is about user not found or invalid credentials
      if (
        error.message.includes('Invalid login credentials') ||
        error.message.includes('User not found') ||
        error.message.includes('Email not confirmed')
      ) {
        // For email not confirmed, provide specific guidance
        if (error.message.includes('Email not confirmed')) {
          throw new Error(
            'Please check your email and click the confirmation link before signing in.',
          );
        }

        // For invalid credentials, provide helpful guidance
        throw new Error(
          'No account found with this email, or incorrect password. Try "Get started" to create an account.',
        );
      }

      // For other errors, throw the original error
      throw error;
    }
  }, [email, password]);

  const handleSignUp = useCallback(async () => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/login/confirm?none=none`,
      },
    });

    // If signup fails because user already exists, show error
    if (error) {
      // Check if the error is about user already existing
      if (
        error.message.includes('already registered') ||
        error.message.includes('already exists') ||
        error.message.includes('User already registered')
      ) {
        throw new Error(
          'This email is already registered. Please sign in instead or use password reset if you forgot your password.',
        );
      }

      // If it's a different signup error, throw the original error
      throw error;
    }

    // Supabase sometimes returns success even for existing users (depending on config)
    // Try to sign in to check if user already exists
    const { error: signInError } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    // If sign-in succeeds, user already existed with this exact password
    if (!signInError) {
      // Sign them out since they tried to sign up, not sign in
      await supabase.auth.signOut();
      throw new Error(
        'This email is already registered. Please sign in instead or use password reset if you forgot your password.',
      );
    }

    // Check the type of sign-in error
    if (signInError.message.includes('Email not confirmed')) {
      // This is a legitimate new signup waiting for confirmation
      return {
        text: 'Check your email to confirm your account',
        type: 'success' as const,
      };
    }

    // If sign-in failed for any other reason (e.g., wrong password), user exists
    if (
      signInError.message.includes('Invalid login credentials') ||
      signInError.message.includes('Invalid')
    ) {
      throw new Error(
        'This email is already registered. Please sign in instead or use password reset if you forgot your password.',
      );
    }

    // Legitimate new signup
    return {
      text: 'Check your email to confirm your account',
      type: 'success' as const,
    };
  }, [email, password]);

  const handleForgotPassword = useCallback(async () => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/login`,
    });
    if (error) throw error;
    return {
      text: 'Check your email for the password reset link',
      type: 'success' as const,
    };
  }, [email]);

  const handleUpdatePassword = useCallback(async () => {
    const { error } = await supabase.auth.updateUser({
      password: password,
    });
    if (error) throw error;
    navigate('/', { replace: true });
  }, [password, navigate]);

  const handleGoogleSignIn = useCallback(async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/login`,
        queryParams: {
          prompt: 'select_account',
        },
      },
    });
    if (error) throw error;
  }, []);

  const submitForm = useCallback(
    async (view: AuthView): Promise<Message> => {
      setLoading(true);
      try {
        switch (view) {
          case 'sign_in':
            await handleSignIn();
            return null;
          case 'sign_up':
            return await handleSignUp();
          case 'forgotten_password':
            return await handleForgotPassword();
          case 'update_password':
            await handleUpdatePassword();
            return null;
          default:
            throw new Error('Invalid auth view');
        }
      } catch (err) {
        let errorMessage = err instanceof Error ? err.message : 'An error occurred';
        // Replace Supabase's generic banned message with a friendlier one
        if (errorMessage.toLowerCase().includes('user is banned')) {
          errorMessage =
            'Your account is currently unavailable, please get in touch with support@jua.ai';
        }
        return {
          text: errorMessage,
          type: 'warn',
        };
      } finally {
        setLoading(false);
      }
    },
    [handleSignIn, handleSignUp, handleForgotPassword, handleUpdatePassword],
  );

  return {
    email,
    setEmail,
    password,
    setPassword,
    loading,
    resetForm,
    submitForm,
    handleGoogleSignIn,
  };
}
